import type { TokenAgeResponse } from '@/pages/api/token-age';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchWithJwt, getFullURL, handleResponse } from '../utils/fetcher';
import { normalizeTokenAddress, tokenKey } from '../utils/normalizeAddress';

/**
 * Builds the normalized query key (sorted, lowercased-EVM `address:chainId` list) so
 * every caller produces the same cache entry regardless of input order or casing.
 */
export const tokenAgeQueryKey = (
  tokens: Array<{ address: string; chainId: number }> | undefined,
) => {
  if (!tokens || tokens.length === 0) return ['token-age', null] as const;

  const sorted = [...tokens].sort((a, b) => {
    const chainDiff = a.chainId - b.chainId;
    if (chainDiff !== 0) return chainDiff;
    return normalizeTokenAddress(a.address, a.chainId).localeCompare(
      normalizeTokenAddress(b.address, b.chainId),
    );
  });

  return ['token-age', sorted.map((t) => tokenKey(t.address, t.chainId)).join(',')] as const;
};

// Auto-slippage quote builders hold their fetch on this query settling, so a hung
// request must fail fast (-> no escalation, longtail 200) instead of stalling quotes.
const TOKEN_AGE_TIMEOUT_MS = 3_000;

// Pool creation time is immutable; the server-side negative cache is what picks up a
// brand-new token, so repeat mounts only need modest staleness here.
const TOKEN_AGE_STALE_TIME_MS = 5 * 60 * 1000;

const fetchTokenAge = (tokensParam: string, signal?: AbortSignal): Promise<TokenAgeResponse> => {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  signal?.addEventListener('abort', onAbort);
  const timeoutId = setTimeout(() => controller.abort(), TOKEN_AGE_TIMEOUT_MS);

  return fetchWithJwt(getFullURL(`/api/token-age?tokens=${tokensParam}`), {
    signal: controller.signal,
  })
    .then(handleResponse<TokenAgeResponse>)
    .finally(() => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
    });
};

/**
 * Fetches the earliest-pool creation time for a batch of tokens (the Auto slippage
 * age-tier escalation signal).
 */
export const useTokenAge = (tokens: Array<{ address: string; chainId: number }> | undefined) => {
  const queryKey = useMemo(() => tokenAgeQueryKey(tokens), [tokens]);
  const tokensParam = queryKey[1];

  const { data, error, isLoading, isFetching, refetch } = useQuery<TokenAgeResponse, Error>({
    queryKey,
    queryFn: ({ signal }) => fetchTokenAge(tokensParam as string, signal),
    enabled: tokensParam !== null,
    placeholderData: keepPreviousData,
    staleTime: TOKEN_AGE_STALE_TIME_MS,
    retry: false,
    refetchOnReconnect: false,
  });

  return { data, error, isLoading, isFetching, refetch };
};
